import config from 'config'
import displayFace from 'flows/start/brand/fonts/PPRightGrotesk-Medium.woff'
import { DESKTOP_QUERY } from 'flows/start/ui/viewport'
import { parseRetryAfter, readRetry } from 'flows/start/readRetry'
import { EXPERIMENT_STALE_MS, experimentIdOf, experimentQueryKey, experimentRetry } from 'flows/start/experiment'

/**
 * /start's first requests, sent from the app's entry so they run while the flow's code downloads
 * instead of after it has arrived and drawn. Part of every page's first load, so it stays small:
 * plain fetch rather than axios, and no React. The flow's hooks read the same queries, under the
 * keys, staleTimes and retries here (hooks.js).
 */

export const isStartPath = (pathname) => /^\/start(\/|$)/.test(pathname)

export const GEOS_QUERY_KEY = ['start', 'geos']
export const GEOS_STALE_MS = 10 * 60 * 1000
export const GEOS_RETRIES = 2

/** A campaign's copy (`?cpy=`), which doesn't change over a visit. */
export const campaignQueryKey = (selector) => ['start', 'join-flow-config', selector]
export const CAMPAIGN_STALE_MS = 10 * 60 * 1000
export const CAMPAIGN_RETRIES = 1

// The same JSON body that the flow's axios client gives its hooks, and on a failure the same
// `status` and `retryAfterMs` its errors carry, so a 429 is retried here as it is there.
const getJson = (url) => fetch(url).then(response => {
  if (!response.ok) {
    const error = new Error(`${url} answered ${response.status}`)
    error.status = response.status
    error.retryAfterMs = parseRetryAfter(response.headers?.get?.('Retry-After'))
    throw error
  }
  return response.json()
})

const appendLink = (attributes) => {
  const link = document.createElement('link')
  Object.entries(attributes).forEach(([name, value]) => link.setAttribute(name, value))
  document.head.appendChild(link)
}

// Where the city step's map gets its style, fonts and tiles (Mapbox GL fetches them with CORS).
const MAPBOX_ORIGIN = 'https://api.mapbox.com'

const isDesktop = () => typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches

/**
 * The flow's code, the cities, any campaign's copy or experiment's offer and the display face, all
 * asked for at once.
 * The two queries retry as their hooks do: a step that opens while one is still out waits on
 * that request, retries and all, rather than sending its own. One that has already failed is
 * made again by the step that needs it.
 */
export const prefetchStart = (queryClient) => {
  // Titles wait for the display face (font-display: block): until now it was found only once the
  // flow's CSS and code had run.
  appendLink({ rel: 'preload', as: 'font', type: 'font/woff', href: displayFace, crossorigin: 'anonymous' })
  // A laptop's city step draws its map once the list has painted (CityStep): the connection is
  // open by then. The map itself, and Mapbox's code, never come with the landing.
  if (isDesktop()) appendLink({ rel: 'preconnect', href: MAPBOX_ORIGIN, crossorigin: 'anonymous' })

  // The router asks for the same chunk as it draws, and gets this download.
  import('flows/start').catch(() => {})

  queryClient.prefetchQuery({
    queryKey: GEOS_QUERY_KEY,
    queryFn: () => getJson(`${config.PROVIDER_KAPI_URL}/locations/public-geos`),
    staleTime: GEOS_STALE_MS,
    ...readRetry({ retry: GEOS_RETRIES })
  })

  const cpy = new URLSearchParams(window.location.search).get('cpy')
  if (cpy) {
    queryClient.prefetchQuery({
      queryKey: campaignQueryKey(cpy),
      queryFn: () => getJson(`${config.MARKETING_KAPI_URL}/join-flow-config/selector/${cpy}`),
      staleTime: CAMPAIGN_STALE_MS,
      ...readRetry({ retry: CAMPAIGN_RETRIES })
    })
  }

  // An ad's experiment (`?eid=`): the city step waits on it to know whether to ask for a city.
  const experimentId = experimentIdOf(window.location.search)
  if (experimentId) {
    queryClient.prefetchQuery({
      queryKey: experimentQueryKey(experimentId),
      queryFn: () => getJson(`${config.MARKETING_KAPI_URL}/experiments/external/${encodeURIComponent(experimentId)}`),
      staleTime: EXPERIMENT_STALE_MS,
      ...readRetry({ retry: experimentRetry })
    })
  }
}
